import { Box, Flex, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { ICON_PACK_OPENED } from 'constants/mixpanel';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { UserType } from 'modules/User';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconColorOption } from 'types/icon.types';
import { capitalizeStr } from 'utils/capitalizeStr';
import { NewWidget } from 'widgets/Widget.types';

import { PartialIconMetadata } from '../IconWidgetMenu.types';
import { IconPack } from './IconPack';
import { IconPackList } from './IconPackList';
import { IconPackData } from './IconView.types';
import { useIconPackLoader } from './useIconPackLoader';

/**
 * Icon Pack View component
 *
 * Renders default icon view when no search query is present.
 * Will render a list of clickable icon pack previews.
 */
interface IconPackViewProps {
  onClickIconWidget: (
    iconId: string,
    viewBox: string,
    mono?: boolean,
    iconMetadata?: { iconPath?: string; isIllustration?: boolean },
  ) => void;
  show: boolean;
  onOpenModal: () => void;
  createWidgetData: (iconId: string, viewBox: string, mono?: boolean, iconMetadata?: PartialIconMetadata) => NewWidget;
  colorOptions?: IconColorOption[];
  setUpgradeCallback?: (callback: () => void, upgradePlan: UserType) => void;
  disableDragDrop?: boolean;
  allowNavFocus?: boolean;
}

export const IconPackView = ({
  onClickIconWidget,
  show,
  colorOptions = [IconColorOption.All, IconColorOption.Color, IconColorOption.Mono],
  setUpgradeCallback,
  onOpenModal,
  createWidgetData,
  disableDragDrop,
  allowNavFocus,
}: IconPackViewProps): ReactElement => {
  // Persist the icon pack view + prevent lag when clearing icon search
  const style = show ? {} : { display: 'none' };

  const [menuHeight, setMenuHeight] = useState<number>(0);
  const tabPanelRef = useRef<HTMLDivElement>(null);

  const { iconPackData } = useIconPackLoader();

  const [selectedPack, setSelectedPack] = useState<IconPackData | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const [filteredPacks, setFilteredPacks] = useState<{
    [IconColorOption.Color]: IconPackData[];
    [IconColorOption.Mono]: IconPackData[];
    [IconColorOption.All]: IconPackData[];
  }>({ [IconColorOption.Color]: [], [IconColorOption.Mono]: [], [IconColorOption.All]: [] });
  const { t } = useTranslation('editor_widget_menu', {
    useSuspense: false,
    keyPrefix: 'sideMenu.iconsMenu.colorOption',
  });

  const handleClickPack = (iconPack: IconPackData) => {
    setSelectedPack(iconPack);

    Mixpanel.track(ICON_PACK_OPENED.eventName, {
      iconpack_id: iconPack.name,
      iconpack_plan: iconPack.permission === 'default' ? 'Free' : capitalizeStr(iconPack.permission),
      is_diverse: iconPack.isDiverse ? 'TRUE' : 'FALSE',
      iconpack_style: iconPack.isMono ? 'Mono' : 'Color',
    });
  };

  const onChangeTab = (index: number) => {
    setSelectedPack(null);
    setTabIndex(index);
  };

  const handleBack = () => {
    setSelectedPack(null);
  };

  // Icon pack data does not change once loaded, so only filter the icons once
  // instead of everytime a tab is changed
  useEffect(() => {
    const colourPacks = iconPackData.filter((pack) => !pack.isMono);
    const monoPacks = iconPackData.filter((pack) => pack.isMono);

    setFilteredPacks({
      [IconColorOption.Color]: colourPacks,
      [IconColorOption.Mono]: monoPacks,
      [IconColorOption.All]: iconPackData,
    });
  }, [iconPackData]);

  useResizeObserver({
    ref: tabPanelRef,
    onResize: ({ height = 0 }) => {
      // Only need to set menuHeight once since all tabs have the same height
      if (menuHeight > 0) return;
      setMenuHeight(height);
    },
  });

  // if a non-mono pack is selected and the color option is changed to mono deselect the pack
  const isOnlyMono = colorOptions.length === 1 && colorOptions[0] === IconColorOption.Mono;
  if (selectedPack && !selectedPack.isMono && isOnlyMono) setSelectedPack(null);

  // reset tab index if it is out of bounds
  if (tabIndex >= colorOptions.length) setTabIndex(0);

  const boxShadow = allowNavFocus ? 'inset var(--vg-shadows-outline)' : 'none';

  return (
    <Flex direction={'column'} overflow={'auto'} sx={style} flexGrow={1} h='full'>
      <Tabs
        display='flex'
        flexDir='column'
        flex='1'
        index={tabIndex}
        onChange={onChangeTab}
        overflow={'hidden'}
        minH={!selectedPack ? 'full' : 10}
        isLazy
        variant='solid'
      >
        <TabList h={10} data-testid={'icon-pack-style-tab-menu'} px='4' gap='1' pb='2'>
          {colorOptions.map((iconColorOption: IconColorOption, index: number) => (
            <Tab
              key={iconColorOption}
              fontSize='sm'
              fontWeight='semibold'
              textTransform='capitalize'
              _focus={{ boxShadow }}
              px='3'
              py='1.5'
              h='32px'
              borderRadius='4px'
              color={tabIndex === index ? 'var(--vg-colors-blue-600)' : 'inherit'}
            >
              {t(iconColorOption as keyof typeof t)}
            </Tab>
          ))}
        </TabList>
        <TabPanels flex={1} overflow='auto'>
          {colorOptions.map((iconColorOption: IconColorOption) => (
            <TabPanel
              overflowX='hidden'
              overflowY='auto'
              key={iconColorOption}
              display={!selectedPack ? 'block' : 'none'}
              _focus={{ boxShadow }}
              p={4}
              pt={0}
              pr={0}
              h={!selectedPack ? 'full' : 0}
            >
              <Box h='full' ref={tabPanelRef} data-testid='iconwidgetmenu-icon-pack-list'>
                <IconPackList
                  packs={filteredPacks[iconColorOption]}
                  onSelectPack={handleClickPack}
                  show={!selectedPack}
                  testId={'iconwidgetmenu-icon-pack-list'}
                  menuHeight={menuHeight}
                />
              </Box>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      {show && (
        <IconPack
          iconPackData={selectedPack}
          onExitView={handleBack}
          onClickIconWidget={onClickIconWidget}
          setUpgradeCallback={setUpgradeCallback}
          onOpenModal={onOpenModal}
          createWidgetData={createWidgetData}
          disableDragDrop={disableDragDrop}
        />
      )}
    </Flex>
  );
};
